import {
    Component,
    ViewChild,
    Input, OnDestroy,
    OnInit,
    Injector,
    QueryList,
    ElementRef,
    ViewChildren,
    ChangeDetectorRef
} from '@angular/core';

import { Subscription, Observable, BehaviorSubject } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { FilterWrapperComponent } from '../filterWrapper/filter-wrapper.component';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FilterService } from '../../services/abstract-filter.service';

import { FilterTypeModel } from '../../models/abstract-filter-type.model';
import { ChipModel } from '../../../Forms/models/chip.model';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FilterSectionEnum } from '../../enums/filter-section.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { FilterGroupModel } from '../../models/filter-group.model';
import { IncludeExcludeEnum } from '../../enums/include-exclude.enum';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { SavedFilterCard, SavedFilterTypeModel } from '../SavedFilterWrapper/saved-filter-type.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { SavedFilter } from '../../models/saved-filter.model';
import { SavedFilterSetupMode } from '../../../../Monitor/Shared/enums/saved-filter-setup-mode.enum';
import { SavedFilterService } from '../../../../Common/Filter/services/saved-filter.service';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { monitoringFiltersEnum } from '../../../../Monitor/Shared/enums/monitoring-filters.enum';
import { MonitoringKeywordFilterModel } from '../../../../Monitor/Shared/models/monitoring-keyword-filter.model';

export enum FilterWidgetActionTypeEnum {
    APPLY = 'apply',
    CANCEL = 'cancel'
}
export interface FilterWidgetCloseAction {
    action: FilterWidgetActionTypeEnum;
}

@Component({
    selector: 'agpr-filter-widget',
    templateUrl: './filter-widget.component.html',
    styleUrls: ['./filter-widget.component.scss'],
    standalone: false
})
export class FilterWidgetComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() id: string = 'filterWidget';
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild(FilterWrapperComponent) filterWrapper: FilterWrapperComponent;
    @ViewChildren('filterChipContainer') set filterChipContainer (containerList: QueryList<ElementRef>) {
        if (containerList) {
            this.hasFilters = containerList?.length > 0;
            this.cd.detectChanges();
        }
    };

    @ViewChildren('conflictingSavedFilterMessage') set conflictingSavedFilterMessage (containerList: QueryList<ElementRef>) {
        if (containerList) {
            this.hasConflictingSavedFilters = containerList?.length > 0;
            this.cd.detectChanges();
        }
    };

    filters: { [key: string]: FilterTypeModel[] } = {};
    filterSections: FilterSectionEnum[] = [];
    filtersWithoutSection: FilterTypeModel[] = [];

    flatFilters: FilterTypeModel[];

    activeFilter: FilterTypeModel;
    hasFilters: boolean = false;
    hasConflictingSavedFilters: boolean = false;
    savedFiltersType: SavedFilterTypeModel;
    isRefMedia: boolean = false; // Le Devoir
    keywordFilter: MonitoringKeywordFilterModel;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly includeExclude = IncludeExcludeEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        private configService: ConfigService,
        public coreDialogService: CoreDialogService,
        protected filterService: FilterService,
        protected injector: Injector,
        private cd: ChangeDetectorRef,
        protected savedFilterService: SavedFilterService
    ) {
        super(coreDialogService, 'FilterWidget');
        this.isRefMedia = this.configService.brandGUIDIsRefMedia();
    }

    ngOnInit() {
        this.subscriptions.push(
            this.filterService.getFilters().subscribe(
                (allFilters) => {
                    this.filterSections = [];
                    this.filtersWithoutSection = [];

                    // For Le Devoir, hide the Discover Lists filter
                    if (this.isRefMedia) {
                        allFilters = allFilters.filter(filter => filter.id !== monitoringFiltersEnum.discoverLists);
                    }

                    for (const section of Object.values(FilterSectionEnum)) {
                        this.filters[section] = allFilters.filter((filter) => filter.section === section);

                        if (this.filters[section].length) {
                            this.filterSections.push(section);
                        }
                    }

                    this.filtersWithoutSection = allFilters.filter((filter) => !filter.section);

                    this.savedFiltersType = allFilters.find(filter => filter.id === this.filterService.savedFilters.id) as SavedFilterTypeModel;

                    if (this.savedFiltersType) {
                        this.filtersWithoutSection = this.filtersWithoutSection.filter(x => x.id !== this.savedFiltersType.id);
                    }

                    this.keywordFilter = allFilters.find((filter) => filter.id === 'keywords') as MonitoringKeywordFilterModel;

                    if (!this.activeFilter) {
                        if (this.filterSections.length) {
                            // default active filter set to keywords
                            this.activeFilter = this.filters[FilterSectionEnum.CONTENT].find(filter => filter.id === 'keywords');
                        } else {
                            this.activeFilter = this.filtersWithoutSection[0];
                        }
                    } else if (this.filterWrapper) {
                        this.filterWrapper.refresh();
                    }
                }
            ),
            this.filterService.getFlatFilters().subscribe(
                (flatFilters) => {
                    this.flatFilters = flatFilters;
                }
            ),
            this.savedFilterService.actionCompleted$.subscribe(action => {
                if (action?.actionType === ResourceActionTypes.CREATE) {
                    this.activeFilter = this.savedFiltersType;
                }
            })
        );
    }

    /**
     * Applies the current filters and hides the panel
     */
    apply() {
        this.filterService.applyFilters();
        this.closeDialog({ action: FilterWidgetActionTypeEnum.APPLY } as FilterWidgetCloseAction);
    }

    /**
     * Removes a chip from a specified filter
     * @param filter A filter
     * @param chip A chip
     */
    removeChip(filter: FilterTypeModel, chip: ChipModel): void {
        filter.removeCurrentChip(chip);

        if (this.filterWrapper) {
            this.filterWrapper.refresh();
        }
    }

    /**
     * Selects a filter type to show the user
     * @param filter A filter type
     */
    selectFilterType(filter: FilterTypeModel, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.activeFilter = filter;
    }

    /**
     * Cancels all of the current filters
     */
    clear() {
        for (const section of this.filterSections) {
            this.filters[section]?.forEach((contentFilter) => {
                if (contentFilter instanceof FilterGroupModel) {
                    contentFilter.children.forEach(child => child.clear());
                } else {
                    contentFilter.clear();
                }
            });
        }
        this.filtersWithoutSection.forEach(filter => filter.clear());

        if (this.savedFiltersType) {
            this.savedFiltersType.clear();
        }

        if (this.filterWrapper) {
            this.filterWrapper.refresh();
        }
    }

    cancel(visibility: boolean = false) {
        if (!visibility) {
            this.closeDialog({ action: FilterWidgetActionTypeEnum.CANCEL } as FilterWidgetCloseAction);
        }
    }

    openSaveFilterDialog() {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer
            }
        };
        this.coreDialogService.open('SaveFilterDialogComponent', io, this.injector);
    }

    openManageSavedFilterDialog() {
        this.coreDialogService.open('SavedFilterManageDialogComponent', {}, this.injector);
    }

    openSavedFilterEditDialog(card: SavedFilterCard) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                mode: SavedFilterSetupMode.edit,
                savedFilterSummary: card.value as SavedFilter
            }
        };
        this.coreDialogService.open('SavedFilterSetupDialogComponent', io, this.injector);
    }

    ungroupSavedFilter(card: SavedFilterCard) {
        this.filterService.ungroupSavedFilter(card.value);
        this.removeFilterCard(card);
    }

    removeFilterCard(card: SavedFilterCard) {
        this.savedFiltersType.removeFilterCard(card);
        if (this.filterWrapper) {
            this.filterWrapper.refresh();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
